/**
 * Eine Ebene an etwas im Bild hängen (S13).
 *
 * Sascha: „Ein Gesicht verfolgen und den Weichzeichner mitziehen — oder eine
 * Bauchbinde daran hängen.“ Das Zweite geht damit: die Ebene wird dorthin
 * gesetzt, wo das Motiv ist, und von dort aus verfolgt; heraus kommen
 * Keyframes, also genau das, womit sich eine Ebene ohnehin bewegt. Wer das
 * Ergebnis nicht mag, zieht einzelne Punkte von Hand -- es ist nichts
 * Besonderes daran.
 *
 * Gelesen wird aus einem *eigenen* Videoelement und nicht aus der Vorschau:
 * die Vorschau gehört dem Abspielkopf, und wer an ihr spult, reißt sie unter
 * dem Betrachter weg. Das eigene Element steht unsichtbar daneben, wird
 * gesucht, gezeichnet, gelesen und danach weggeräumt.
 *
 * Gerechnet wird auf einem verkleinerten Bild (320 Punkte breit). Das ist
 * nicht Sparsamkeit um ihrer selbst willen: ein Kopf, der sich um zehn Punkte
 * bewegt, bewegt sich im kleinen Bild um zwei, und ein kleineres Suchfenster
 * ist quadratisch schneller. Für die Genauigkeit reicht es -- die Ebene folgt
 * einem Gesicht, sie vermisst es nicht.
 */

import { bildAusschnitt, verfolge, zuGrau, type Grau, type VerfolgungsGrund } from '@schnitt/shared';

/** Breite, auf die für die Rechnung verkleinert wird. */
const ARBEITSBREITE = 320;
/** So viele Punkte je Sekunde entstehen. Vier reichen dem Auge und dem Gerät. */
export const PUNKTE_JE_SEKUNDE = 4;
/** Wie weit in einem Schritt gesucht wird, in Punkten des kleinen Bildes. */
const SUCHRADIUS = 12;
/** Die Kantenlänge der Vorlage im kleinen Bild. */
const VORLAGE = 40;

export interface Punkt {
  /** Sekunden seit Beginn des Clips. */
  at: number;
  /** Mitte der Ebene, 0..1 der Leinwand. */
  x: number;
  y: number;
}

export interface Ergebnis {
  punkte: Punkt[];
  /** Warum aufgehört wurde, wenn nicht das Ende erreicht war. */
  grund?: VerfolgungsGrund | 'kein-bild';
  /** Bis wohin verfolgt wurde, in Sekunden seit Beginn des Clips. */
  bis: number;
}

/** Ein unsichtbares Element, das gesucht werden darf, ohne jemanden zu stören. */
async function leihVideo(src: string): Promise<HTMLVideoElement> {
  const el = document.createElement('video');
  el.src = src;
  el.muted = true;
  el.playsInline = true;
  el.preload = 'auto';
  el.crossOrigin = 'anonymous';
  el.style.position = 'fixed';
  el.style.left = '-9999px';
  el.style.width = '1px';
  el.style.height = '1px';
  document.body.appendChild(el);
  await new Promise<void>((fertig, schiefgelaufen) => {
    const gut = () => fertig();
    const schlecht = () => schiefgelaufen(new Error('Die Vorschau ließ sich nicht öffnen.'));
    el.addEventListener('loadedmetadata', gut, { once: true });
    el.addEventListener('error', schlecht, { once: true });
  });
  return el;
}

/** An eine Stelle springen und warten, bis das Bild wirklich dort steht. */
function springe(el: HTMLVideoElement, zeit: number): Promise<void> {
  return new Promise((fertig) => {
    if (Math.abs(el.currentTime - zeit) < 1e-3 && el.readyState >= 2) {
      fertig();
      return;
    }
    el.addEventListener('seeked', () => fertig(), { once: true });
    el.currentTime = zeit;
  });
}

/**
 * Verfolgt, was unter der Ebene liegt, vom aktuellen Punkt aus nach vorn.
 *
 * `von` und `bis` sind Sekunden *in der Datei*; `x`/`y` ist die Mitte der
 * Ebene auf der Leinwand (0..1). Das Ergebnis sind Punkte in Sekunden seit
 * Beginn des Clips -- so, wie Keyframes gezählt werden.
 */
export async function verfolgeImVideo(opts: {
  src: string;
  /** Medienzeit, an der es losgeht. */
  von: number;
  /** Medienzeit, an der Schluss ist. */
  bis: number;
  /** Wie viel Clipzeit einer Sekunde Medienzeit entspricht (1 / Tempo). */
  clipProMedien: number;
  /** Wo der erste Punkt liegt, in Clipsekunden. */
  ersterPunkt: number;
  x: number;
  y: number;
  /** Wird nach jedem Schritt gerufen: 0..1. */
  fortschritt?: (anteil: number) => void;
}): Promise<Ergebnis> {
  const { src, von, bis, clipProMedien, ersterPunkt, x, y } = opts;
  const punkte: Punkt[] = [{ at: ersterPunkt, x, y }];
  if (bis <= von) return { punkte, bis: ersterPunkt };

  // Kann das Gerät die Vorschau gar nicht öffnen -- dieser Prüf-Chromium
  // etwa dekodiert kein H.264 --, ist das kein Fehler, sondern eine Auskunft.
  let el: HTMLVideoElement;
  try {
    el = await leihVideo(src);
  } catch {
    return { punkte, bis: ersterPunkt, grund: 'kein-bild' };
  }
  const wegraeumen = () => {
    el.removeAttribute('src');
    el.load();
    el.remove();
  };
  try {
    const breite = ARBEITSBREITE;
    const hoehe = Math.max(1, Math.round((el.videoHeight / Math.max(1, el.videoWidth)) * breite));
    const leinwand = document.createElement('canvas');
    leinwand.width = breite;
    leinwand.height = hoehe;
    const stift = leinwand.getContext('2d', { willReadFrequently: true });
    if (!stift) return { punkte, bis: ersterPunkt, grund: 'kein-bild' };

    const lies = async (zeit: number): Promise<Grau | null> => {
      await springe(el, zeit);
      // Ein Element, das nichts dekodiert hat, malt ein leeres Bild -- und
      // ein leeres Bild ist die flachste Vorlage, die es gibt. Lieber gleich
      // sagen, dass hier nichts zu holen ist.
      if (el.readyState < 2 || el.videoWidth === 0) return null;
      stift.drawImage(el, 0, 0, breite, hoehe);
      try {
        const daten = stift.getImageData(0, 0, breite, hoehe).data;
        return zuGrau(daten, breite, hoehe);
      } catch {
        // Fremde Herkunft: die Leinwand ist dann unlesbar.
        return null;
      }
    };

    const erstes = await lies(von);
    if (!erstes) return { punkte, bis: ersterPunkt, grund: 'kein-bild' };
    let vorlage = bildAusschnitt(erstes, x * breite - VORLAGE / 2, y * hoehe - VORLAGE / 2, VORLAGE, VORLAGE);
    let mitteX = x * breite;
    let mitteY = y * hoehe;

    const schrittMedien = 1 / PUNKTE_JE_SEKUNDE / Math.max(0.01, clipProMedien);
    const gesamt = Math.max(1e-6, bis - von);
    for (let zeit = von + schrittMedien; zeit <= bis + 1e-6; zeit += schrittMedien) {
      const bild = await lies(zeit);
      if (!bild) return { punkte, bis: punkte[punkte.length - 1]?.at ?? ersterPunkt, grund: 'kein-bild' };
      const schritt = verfolge(bild, vorlage, mitteX, mitteY, SUCHRADIUS);
      if (schritt.abbruch) {
        return { punkte, bis: punkte[punkte.length - 1]?.at ?? ersterPunkt, grund: schritt.abbruch };
      }
      mitteX = schritt.mitteX;
      mitteY = schritt.mitteY;
      // Die Vorlage wandert mit: ein Gesicht dreht sich, und eine Vorlage vom
      // ersten Bild passt nach zehn Sekunden auf nichts mehr. Der Preis ist,
      // dass ein Fehler mitwandert -- deshalb die Güte, die das abfängt.
      vorlage = bildAusschnitt(bild, mitteX - VORLAGE / 2, mitteY - VORLAGE / 2, VORLAGE, VORLAGE);
      punkte.push({
        at: ersterPunkt + (zeit - von) * clipProMedien,
        x: Math.max(0, Math.min(1, mitteX / breite)),
        y: Math.max(0, Math.min(1, mitteY / hoehe)),
      });
      opts.fortschritt?.(Math.min(1, (zeit - von) / gesamt));
    }
    return { punkte, bis: punkte[punkte.length - 1]?.at ?? ersterPunkt };
  } finally {
    wegraeumen();
  }
}

/** Was dem Menschen gesagt wird, wenn gar kein Bild zu lesen war. */
export const KEIN_BILD =
  'Dieses Gerät kann die Vorschau nicht dekodieren — verfolgen geht hier nicht. Am Rechner oder auf dem Telefon schon.';
